import apiClient from './apiClient';
import axios from 'axios';
import { Company, CompanyStats, CreateCompanyWithAdminRequest, UpdateCompanyRequest } from '../types';

/**
 * Service for company management
 */
export class CompaniesService {
  /**
   * Get company details
   */
  async getCompany(): Promise<Company> {
    const response = await apiClient.get<Company>('/companies');
    
    if (response.success && response.data) {
      return response.data;
    }
    
    throw new Error(response.message || 'Failed to get company details');
  }

  /**
   * Update company details
   */
  async updateCompany(companyData: UpdateCompanyRequest): Promise<boolean> {
    const response = await apiClient.put<void>('/companies', companyData);
    return response.success;
  }

  /**
   * Get company statistics
   */
  async getCompanyStats(): Promise<CompanyStats> {
    try {
      const response = await apiClient.get<CompanyStats>('/companies/stats');
      
      if (response.success && response.data) {
        return response.data;
      }
      
      // Return default stats instead of throwing an error
      console.error('Failed to get company statistics:', response.message);
      return {
        totalUsers: 0,
        totalAdmins: 0,
        totalManagers: 0,
        totalGuards: 0,
        totalSites: 0,
        totalReports: 0,
        reportsLast24Hours: 0,
        reportsLast7Days: 0,
        reportsLast30Days: 0,
        verifiedReports: 0,
        verificationRate: 0
      };
    } catch (error) {
      console.error('Error fetching company stats:', error);
      // Return default stats
      return {
        totalUsers: 0,
        totalAdmins: 0,
        totalManagers: 0,
        totalGuards: 0,
        totalSites: 0,
        totalReports: 0,
        reportsLast24Hours: 0,
        reportsLast7Days: 0,
        reportsLast30Days: 0,
        verifiedReports: 0,
        verificationRate: 0
      };
    }
  }

  /**
   * Get all companies (SuperAdmin only)
   */
  async getAllCompanies(): Promise<Company[]> {
    const response = await apiClient.get<Company[]>('/companies/all');
    
    if (response.success && response.data) {
      return response.data;
    }
    
    throw new Error(response.message || 'Failed to get all companies');
  }

  /**
   * Create a new company with an admin user (SuperAdmin only)
   */
  async createCompanyWithAdmin(companyData: CreateCompanyWithAdminRequest): Promise<Company> {
    const response = await apiClient.post<Company>('/companies', companyData);
    
    if (response.success && response.data) {
      return response.data;
    }
    
    throw new Error(response.message || 'Failed to create company');
  }

  /**
   * Upload company logo file and get Cloudinary URL
   * Replaces base64 storage with file-based storage
   */
  async uploadCompanyLogo(logoFile: File): Promise<string> {
    try {
      const formData = new FormData();
      formData.append('logoFile', logoFile);

      // Get API base URL and auth token
      const API_URL = typeof window !== 'undefined' && window._env_ && window._env_.REACT_APP_API_URL 
        ? window._env_.REACT_APP_API_URL 
        : 'https://d3t71xc0hyi6iy.cloudfront.net/api';
      
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication token not found');
      }

      // Use direct axios call to bypass API client's JSON serialization
      const response = await axios.post(`${API_URL}/companies/upload-logo`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          // Don't set Content-Type - let axios set it automatically for FormData
        },
        withCredentials: true
      });

      if (response.data?.success) {
        return response.data.logoUrl;
      }
      
      throw new Error(response.data?.message || 'Failed to upload company logo');
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error(error.message || 'Failed to upload company logo');
    }
  }
}

// Create a default instance
export const companiesService = new CompaniesService();

export default companiesService;
